import {CanActivateFn, Params, Router, UrlTree} from '@angular/router';
import {Cookie} from '@ccp/lib/cookie';
import {inject} from '@angular/core';
import {RouterTrackerService} from '@ccp/services/router-tracker/router-tracker.service';
import {UrlParamLiterals} from '@ccp/enums/literals/urlparams.enum';
import {UserAgent} from '@ccp/lib/user-agent';
import {Views} from '@ccp/config/views';
import {WechatService} from '@ccp/services/wechat/wechat.service';
import {UrlManager} from '@ccp/lib/url-manager';
import {ToggleFeatures} from '../enums/toggleFeatures.enum';
import {FeatureToggleService} from '../services/toggle-features/feature-toggle.service';
import {WebStorage} from '../lib/web-storage';
import {WSLiterals} from '../enums/literals/webstorage.enum';
import {UserContextService} from '@ccp/services/app-context/user-context.service';
import {ProductContextService} from '@ccp/services/app-context/product-context.service';

let wechatService: WechatService;
let productContextService: ProductContextService;
let userContextService: UserContextService;
let routerTracker: RouterTrackerService;
let router: Router;
let featureToggleService: FeatureToggleService;

export const AuthGuard: CanActivateFn = (route, state) => {
  wechatService = inject(WechatService);
  productContextService = inject(ProductContextService);
  userContextService = inject(UserContextService);
  routerTracker = inject(RouterTrackerService);
  router = inject(Router);
  featureToggleService = inject(FeatureToggleService);

  const redirection = route.queryParamMap.get(UrlParamLiterals.Redirect);
  const queryParams = {...route.queryParams};
  let returnUrl = state.url;
  // remove redirect path if exist after redirection because we don't need it anymore
  if (redirection) {
    delete queryParams.redirect;
    returnUrl = UrlManager.removeQueryString(returnUrl) + UrlManager.removeParam(UrlParamLiterals.Redirect, returnUrl);
  }

  if (!!featureToggleService.featureConfig$()?.getConfig(ToggleFeatures.hydraSso)) {
    WebStorage.set(WSLiterals.ccpRedirectUrl, returnUrl);
  } else {
    WebStorage.remove(WSLiterals.ccpRedirectUrl);
  }

  handleWechat(state);

  if (isLoggedOrIsPOS()) {
    WebStorage.remove(WSLiterals.ccpRedirectUrl);
    return true;
  }
  if (gotProductOrIsInTunnel() || isSignupRedirectionPreferred(redirection)) {
    return goToRegister(queryParams, returnUrl);
  }
  return goToLogin(queryParams, returnUrl);
};

function handleWechat(state): void {
  if (UserAgent.checkUserAgentIsWeChat() && !!wechatService) {
    Cookie.create(wechatService._CCP_WECHAT_CALLBACK_URL_, state.url);
  }
}

function isLoggedOrIsPOS(): boolean {
  const from = UrlManager.getParam(UrlParamLiterals.From);
  const warrantyUid = UrlManager.getParam(UrlParamLiterals.WarrantyUid);
  const isPos = from.notEmpty() && warrantyUid.notEmpty();
  return isPos || userContextService.currentUser$()?.isLogged;
}

function gotProductOrIsInTunnel(): boolean {
  return !!productContextService.isProductBeingRegistered$() || routerTracker.weAreInTunnel();
}

function isSignupRedirectionPreferred(redirection: string): boolean {
  return redirection === 'signup';
}

function goToRegister(queryParams: Params, returnUrl: string): UrlTree {
  return router.createUrlTree([Views.userRegister.path], {
    queryParams: {...queryParams, returnUrl: returnUrl},
  });
}

function goToLogin(queryParams: Params, returnUrl: string): UrlTree {
  return router.createUrlTree([Views.userLogin.path], {
    queryParams: {...queryParams, returnUrl: returnUrl},
  });
}
